
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TableRow, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  FilterBar,
  SearchInput,
  StatusTabsContainer,
  StyledTabs,
  StatusTab,
  StyledTableContainer,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  CustomStyledTableHead,
  PaginationFooter
} from './styles';
import CustomPagination from '../../components/CustomPagination';
import { AppDispatch } from '../../store';
import { fetchWorkerReports, fetchWorkerReportStatistics, selectWorkerReports, selectWorkerReportsCount, selectWorkerReportsLoading, selectWorkerReportStatistics } from '../../modules/reports';
import Loader from '../../components/common/Loader';
import SortDropdown from '../../components/SortDropdown';
import dayjs from 'dayjs';
import { useSidebar } from '../../context/SidebarContext';
import { useBreadcrumb } from '../../context/BreadcrumbContext';

const OrgReportAnalysis: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { setActivePage } = useSidebar();
  const { setIsShow } = useBreadcrumb();

  useEffect(() => {
    setActivePage("report");
    setIsShow(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const workerReports = useSelector(selectWorkerReports);
  const totalCount = useSelector(selectWorkerReportsCount);
  const loading = useSelector(selectWorkerReportsLoading);
  const statistics = useSelector(selectWorkerReportStatistics);

  // Default to current month
  const [startDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 = All, 1 = Overtime
  const [sortValue, setSortValue] = useState('full_name-asc');
  const [orderBy, order] = sortValue.split('-');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Fetch Statistics
  useEffect(() => {
    dispatch(fetchWorkerReportStatistics({
      start_date: startDate,
      end_date: endDate,
      search: debouncedSearchTerm
    }));
  }, [dispatch, startDate, endDate, debouncedSearchTerm]);

  // Fetch Reports
  useEffect(() => {
    dispatch(fetchWorkerReports({
      start_date: startDate,
      end_date: endDate,
      search: debouncedSearchTerm,
      overtime: tabValue === 1 ? true : false,
      page: page,
      pageSize: rowsPerPage,
      orderBy: orderBy,
      order: order as 'asc' | 'desc'
    }));
  }, [dispatch, startDate, endDate, debouncedSearchTerm, tabValue, page, orderBy, order]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <Box>
      <Loader open={loading} />
      <PageContainer>
        {/* Header */}
        <PageHeader>
          <PageTitle>Workers List</PageTitle>

          {/* Search and Sort */}
          <FilterBar>
            <SearchInput
              placeholder="Search by Name, No, Facility Name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchTerm('')}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <SortDropdown
              activeSortId={sortValue}
              onSortChange={(value) => setSortValue(value)}
              options={[
                { id: 'full_name-asc', label: 'Name (A-Z)' },
                { id: 'full_name-desc', label: 'Name (Z-A)' },
              ]}
            />
          </FilterBar>
        </PageHeader>

        {/* Tabs */}
        <StatusTabsContainer>
          <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="status tabs">
            <StatusTab label={`All (${statistics.total_workers})`} />
            <StatusTab label={`Overtime (${statistics.overtime_workers})`} />
          </StyledTabs>
        </StatusTabsContainer>

        {/* Table */}
        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomStyledTableHead>
              <TableRow>
                <StyledTableCell component="th">No</StyledTableCell>
                <StyledTableCell component="th">Full Name</StyledTableCell>
                <StyledTableCell component="th">Role</StyledTableCell>
                <StyledTableCell component="th">No of Shifts</StyledTableCell>
                <StyledTableCell component="th">Regular Hours</StyledTableCell>
                <StyledTableCell component="th">Overtime Hours</StyledTableCell>
                <StyledTableCell component="th">Regular Pay</StyledTableCell>
                <StyledTableCell component="th">Overtime Pay</StyledTableCell>
                <StyledTableCell component="th">Total Pay</StyledTableCell>
              </TableRow>
            </CustomStyledTableHead>
            <StyledTableBody>
              {(workerReports || []).length === 0 && !loading && (
                <TableRow>
                  <StyledTableCell colSpan={9} align="center">
                    No data available
                  </StyledTableCell>
                </TableRow>
              )}
              {(workerReports || []).map((row) => (
                <TableRow key={row.worker_id} hover>
                  <StyledTableCell>{row.worker_id}</StyledTableCell>
                  <StyledTableCell>{row.full_name}</StyledTableCell>
                  <StyledTableCell>{row.role}</StyledTableCell>
                  <StyledTableCell>{row.no_of_shifts}</StyledTableCell>
                  <StyledTableCell>{row.regular_hours} h</StyledTableCell>
                  <StyledTableCell>{row.overtime_hours > 0 ? `${row.overtime_hours} h` : '-'}</StyledTableCell>
                  <StyledTableCell>${row.regular_pay}</StyledTableCell>
                  <StyledTableCell>{parseFloat(row.overtime_pay) > 0 ? `$${row.overtime_pay}` : '-'}</StyledTableCell>
                  <StyledTableCell><b>${row.total_pay}</b></StyledTableCell>
                </TableRow>
              ))}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>

        {/* Pagination */}
        <PaginationFooter>
          <Box>
            Showing {(workerReports || []).length} of {totalCount} results
          </Box>
          <CustomPagination
            totalPages={totalPages || 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </PaginationFooter>
      </PageContainer>
    </Box>
  );
};

export default OrgReportAnalysis;
